/**
 * The state-tax order status registry (spec 2026-09-30, section 5.1).
 *
 * `service_data.status` moves only along `TRANSITIONS`. The API's one writer
 * (`transitionStateTaxOrder`, apps/api/src/state-tax/transition.ts) checks
 * `canTransition` and keeps `orders.status` / `orders.payment_status` in step
 * with `STATUS_META`.
 */

export const STATE_TAX_STATUSES = [
  "details_needed",
  "quote_review",
  "awaiting_payment",
  "paid",
  "in_progress",
  "filed",
  "closed",
] as const;

export type StateTaxStatus = (typeof STATE_TAX_STATUSES)[number];

export const CLOSE_REASONS = [
  "not_owed",
  "filed_with_state",
  "self_filed",
  "refunded",
  "expired",
  "customer_cancelled",
] as const;

export type CloseReason = (typeof CLOSE_REASONS)[number];

/** The six locked badge tones both apps render (same set as @nt/federal-tax-rules). */
export type StatusTone =
  "success" | "progress" | "pending" | "attention" | "critical" | "special";

/** The `orders.status` values a state-tax order maps to. */
export type OrderStatus = "pending" | "processing" | "completed" | "cancelled";

/** The `orders.payment_status` values a status can decide. */
export type OrderPaymentStatus = "pending" | "paid" | "refunded";

export interface StatusMeta {
  customerLabel: string;
  adminLabel: string;
  tone: StatusTone;
  terminal: boolean;
  /** What the writer keeps `orders.status` at for this status. */
  orderStatus: OrderStatus;
  /**
   * What the writer keeps `orders.payment_status` at, or `null` when the
   * status alone does not decide it (`closed`: see `paymentStatusFor`).
   */
  paymentStatus: OrderPaymentStatus | null;
}

export const STATUS_META: Record<StateTaxStatus, StatusMeta> = {
  details_needed: {
    customerLabel: "Add company details",
    adminLabel: "Details needed",
    tone: "attention",
    terminal: false,
    orderStatus: "pending",
    paymentStatus: "pending",
  },
  quote_review: {
    customerLabel: "We're checking the state amount",
    adminLabel: "Quote review",
    tone: "progress",
    terminal: false,
    orderStatus: "pending",
    paymentStatus: "pending",
  },
  awaiting_payment: {
    customerLabel: "Awaiting payment",
    adminLabel: "Awaiting payment",
    tone: "pending",
    terminal: false,
    orderStatus: "pending",
    paymentStatus: "pending",
  },
  paid: {
    customerLabel: "Paid. We file it next.",
    adminLabel: "Paid, queued",
    tone: "progress",
    terminal: false,
    orderStatus: "processing",
    paymentStatus: "paid",
  },
  in_progress: {
    customerLabel: "Filing with the state",
    adminLabel: "In progress",
    tone: "progress",
    terminal: false,
    orderStatus: "processing",
    paymentStatus: "paid",
  },
  filed: {
    customerLabel: "Filed",
    adminLabel: "Filed",
    tone: "success",
    terminal: true,
    orderStatus: "completed",
    paymentStatus: "paid",
  },
  closed: {
    customerLabel: "Closed",
    adminLabel: "Closed",
    tone: "pending",
    terminal: true,
    orderStatus: "cancelled",
    paymentStatus: null,
  },
};

export interface CloseReasonMeta {
  customerLabel: string;
  adminLabel: string;
  tone: StatusTone;
}

export const CLOSE_REASON_META: Record<CloseReason, CloseReasonMeta> = {
  not_owed: {
    customerLabel: "Closed: nothing was owed",
    adminLabel: "Closed: not owed",
    tone: "pending",
  },
  filed_with_state: {
    customerLabel: "Already filed with the state",
    adminLabel: "Closed: already filed with the state",
    tone: "success",
  },
  self_filed: {
    customerLabel: "You filed this yourself",
    adminLabel: "Closed: customer self-filed",
    tone: "success",
  },
  refunded: {
    customerLabel: "Refunded",
    adminLabel: "Closed: refunded",
    tone: "special",
  },
  expired: {
    customerLabel: "Closed: filing window ended",
    adminLabel: "Closed: expired",
    tone: "pending",
  },
  customer_cancelled: {
    customerLabel: "Canceled",
    adminLabel: "Closed: cancelled by customer",
    tone: "pending",
  },
};

export const TERMINAL_STATUSES: readonly StateTaxStatus[] =
  STATE_TAX_STATUSES.filter((status) => STATUS_META[status].terminal);

export function isTerminal(status: StateTaxStatus): boolean {
  return STATUS_META[status].terminal;
}

/** The customer-facing label; a closed order reads its reason. */
export function customerStatusLabel(
  status: StateTaxStatus,
  reason?: CloseReason | null,
): string {
  if (status === "closed" && reason) {
    return CLOSE_REASON_META[reason].customerLabel;
  }
  return STATUS_META[status].customerLabel;
}

/** The console label; a closed order reads its reason. */
export function adminStatusLabel(
  status: StateTaxStatus,
  reason?: CloseReason | null,
): string {
  if (status === "closed" && reason) {
    return CLOSE_REASON_META[reason].adminLabel;
  }
  return STATUS_META[status].adminLabel;
}

/**
 * `orders.payment_status` for a status. Only a `refunded` close changes it;
 * any other close leaves the current payment status alone (`null`), because
 * whether money was collected before the close is not a fact of the status.
 */
export function paymentStatusFor(
  status: StateTaxStatus,
  reason?: CloseReason | null,
): OrderPaymentStatus | null {
  if (status === "closed") return reason === "refunded" ? "refunded" : null;
  return STATUS_META[status].paymentStatus;
}

/**
 * The transition table. Every open status can close; `paid` reaches `filed`
 * only through `in_progress`, and `quote_review` can go back to
 * `details_needed` when the customer must correct the company details.
 */
export const TRANSITIONS: Record<StateTaxStatus, readonly StateTaxStatus[]> = {
  details_needed: ["quote_review", "closed"],
  quote_review: ["awaiting_payment", "details_needed", "closed"],
  awaiting_payment: ["paid", "closed"],
  paid: ["in_progress", "closed"],
  in_progress: ["filed", "closed"],
  filed: [],
  closed: [],
};

/**
 * Whether a row at `from` may move to `to`. A same-status move is refused and
 * a word outside the registry has no moves.
 */
export function canTransition(from: string, to: StateTaxStatus): boolean {
  if (!(STATE_TAX_STATUSES as readonly string[]).includes(to)) return false;
  if (!(STATE_TAX_STATUSES as readonly string[]).includes(from)) return false;
  return TRANSITIONS[from as StateTaxStatus].includes(to);
}

// ---------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------

export interface InitialStatusInput {
  state: "WY" | "DE";
  entity: "llc" | "corporation";
  /** The state fee the order will charge, in cents (0 when covered). */
  stateFeeCents: number;
  /** Covered by Full Compliance. */
  waived: boolean;
}

/**
 * The status a new order is created with. A Delaware corporation always
 * starts at `details_needed` (its amount is not known yet). Otherwise a waived
 * order with no state fee to collect starts at `paid`, and everything else at
 * `awaiting_payment`.
 */
export function initialStatus(input: InitialStatusInput): StateTaxStatus {
  if (input.state === "DE" && input.entity === "corporation") {
    return "details_needed";
  }
  if (input.waived && input.stateFeeCents === 0) return "paid";
  return "awaiting_payment";
}
